import { MarketoFormModal } from '@/features/MarketoForm';
import { MarketoFormModalProps } from '@/features/MarketoForm/MarketoForm.interface';
import { Theme } from '@/utils/constants';
import { mapLinkToProps } from '@/utils/helpers';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useMemo, useState } from 'react';
import { MuiButton, MuiLink } from '../material-ui';
import { ButtonProps } from './Button.interface';
import useStyles from './Button.styles';
import { withVisibility } from '@/HoC/withVisibility';

const Link = memo(
  ({
    children,
    theme = Theme.light,
    link,
    startIcon,
    endIcon,
    download,
    className: overrideClassName,
    onClick,
    'data-datalayer-type': datalayerType,
    ...props
  }: PropsWithChildren<ButtonProps>) => {
    const classes = useStyles({});
    const linkClassName = clsx(classes.link, `${theme}-theme`, overrideClassName);

    const linkProps = useMemo(() => mapLinkToProps(link), [link]);
    if (!link?.displayText && !children && !startIcon && !endIcon) {
      return null;
    }

    return (
      <MuiLink
        onClick={onClick}
        rel="noopener"
        {...linkProps}
        download={download}
        data-datalayer-type={datalayerType}
        className={linkClassName}
        {...props}
      >
        {startIcon}
        {children ||
          (link?.displayText && <span dangerouslySetInnerHTML={{ __html: link?.displayText }} />)}
        {endIcon}
      </MuiLink>
    );
  },
);

const Button = memo(
  ({
    children,
    theme = Theme.light,
    outlined,
    startIcon,
    endIcon,
    link,
    className: overrideClassName,
    download,
    onClick,
    disabled,
    type,
    size,
    variant = 'button',
    'data-testid': dataTestId,
    fullWidth = false,
    'data-datalayer-type': datalayerType,
    'data-datalayer-ignore-cta': datalayerIgnoreCTA,
    ...restProps
  }: PropsWithChildren<ButtonProps>) => {
    const classes = useStyles({});
    const buttonVariant = React.useMemo(() => {
      if (/text/.test(variant)) return 'text';
      if (outlined) return 'outlined';
      return 'contained';
    }, [variant, outlined]);

    const buttonProps = {
      color: 'secondary',
      disableElevation: true,
      variant: buttonVariant,
      startIcon,
      endIcon,
      download,
      onClick,
      type,
      disabled,
      fullWidth,
      'data-testid': dataTestId,
      'data-datalayer-type': datalayerType,
      'data-datalayer-ignore-cta': datalayerIgnoreCTA,
    };
    const linkProps = useMemo(() => mapLinkToProps(link), [link]);
    const buttonClassName = clsx(
      outlined && classes.outlined,
      !outlined && buttonVariant !== 'text' && classes.contained,
      buttonVariant === 'text' ? classes.link : classes.button,
      `${theme}-theme`,
      overrideClassName,
      'button',
    );

    if (!link?.displayText && !children && !startIcon && !endIcon) {
      return null;
    }

    return (
      <MuiButton
        size={size}
        {...buttonProps}
        {...linkProps}
        className={buttonClassName}
        {...restProps}
      >
        {link?.displayText ? (
          <span dangerouslySetInnerHTML={{ __html: link?.displayText }} />
        ) : (
          children
        )}
      </MuiButton>
    );
  },
);

const withMarketoModal = (Component: any, components: any) =>
  React.memo(({ link, children, isOpenedByCTAVariant, ...props }: PropsWithChildren<any>) => {
    // const { MarketoFormModal } = components;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [dataLayerAttributes, setDataLayerAttributes] = React.useState<Record<string, string>>({
      variant: '',
      position: '',
      linkText: '',
    });

    const handleOpenModal = React.useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setIsOpen(true);

      try {
        const variant =
          (event?.target as HTMLAnchorElement)
            ?.closest('[data-datalayer-variant]')
            ?.getAttribute('data-datalayer-variant') || '';
        const position =
          (event?.target as HTMLAnchorElement)
            ?.closest('[data-datalayer-position]')
            ?.getAttribute('data-datalayer-position') || '';

        const linkText = (event?.target as HTMLAnchorElement)?.textContent?.trim() || '';

        setDataLayerAttributes({ position, variant, linkText });
        // eslint-disable-next-line no-empty
      } catch (error) {}
    }, []);

    const handleCloseModal = () => {
      setIsOpen(false);
    };

    const handleSuccess = () => {
      setIsOpen(false);
    };

    const { marketoLink } = link;
    const {
      baseUrl,
      confirmationMessage,
      formId,
      munchkinId,
      rules,
      recaptchaSitekey,
      recaptchaLegal,
      heading,
      formDescription,
      emailBlacklistErrorMessage,
      emailFieldname,
      emailsBlacklisted,
    } = marketoLink;

    const marketoFormProps: MarketoFormModalProps = {
      baseUrl,
      confirmationMessage,
      formId,
      munchkinId,
      onSuccess: handleSuccess,
      isOpen,
      onClose: handleCloseModal,
      rules,
      recaptchaSitekey,
      recaptchaLegal,
      heading,
      formDescription,
      datalayerVariant: dataLayerAttributes.variant,
      datalayerPosition: dataLayerAttributes.position,
      datalayerLinkText: dataLayerAttributes.linkText,
      emailBlacklistErrorMessage,
      emailFieldname,
      emailsBlacklisted,
      isOpenedByCTAVariant,
    };

    return (
      <>
        <Component onClick={handleOpenModal} link={link} {...props}>
          {children}
        </Component>
        <MarketoFormModal {...marketoFormProps} />
      </>
    );
  });

const ButtonLink = ({ children, variant, link, ...props }: PropsWithChildren<ButtonProps>) => {
  const variantType = React.useMemo(() => {
    const linkVariant = /highlighted/.test(link?.buttonStyle || '') ? 'link' : 'button';
    return (variant || linkVariant) === 'link' ? 'link' : 'button';
  }, [link?.buttonStyle, variant]);
  const isLink = React.useMemo(() => variantType === 'link', [variantType]);
  const VariantComponent = React.useMemo(() => {
    return isLink ? Link : withVisibility(Button, ['link', 'visibility']);
  }, [isLink]);

  const VariantWithMarketo = withMarketoModal(VariantComponent, { MarketoFormModal });
  const isOutlined = React.useMemo(
    () => props?.outlined || link?.buttonStyle === 'secondary',
    [link?.buttonStyle, props?.outlined],
  );
  const variantProps = React.useMemo(
    () =>
      isLink
        ? {}
        : {
            variant,
            outlined: isOutlined,
          },
    [isLink, isOutlined, variant],
  );

  if (link?.marketoLink?.formId) {
    return (
      <VariantWithMarketo
        link={link}
        data-datalayer-cta-type="popup_form"
        data-datalayer-link-type="popup_form"
        data-datalayer-ignore-link
        isOpenedByCTAVariant={variantType}
        {...props}
        {...variantProps}
      >
        {children}
      </VariantWithMarketo>
    );
  }

  return (
    <VariantComponent link={link} {...props} {...variantProps}>
      {children}
    </VariantComponent>
  );
};

export default memo(ButtonLink);
